import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Briefcase, ChevronRight, ArrowRight, Zap, Users, Send, Sparkles } from 'lucide-react';
import { PROGRAMS } from '../data';
import Modal from '../components/Modal';
import Seo from '../components/Seo';
import { PAGE_METADATA, getProgramsStructuredData } from '../seo/pageMetadata';
import { sendToPrivyr } from '../utils/sendToPrivyr';

const Programs = () => {
  const [isCapitalModalOpen, setIsCapitalModalOpen] = useState(false);
  const [isMentorshipModalOpen, setIsMentorshipModalOpen] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const source = form.dataset.source || 'Website - Programs';

    if (source === 'Website - Startup Capital') {
      sendToPrivyr({
        name: String(formData.get('fullName') || ''),
        email: String(formData.get('email') || ''),
        message: [
          `Business Name: ${String(formData.get('businessName') || '')}`,
          `Business Description: ${String(formData.get('businessDescription') || '')}`,
          `Funding Goal (NGN): ${String(formData.get('fundingGoal') || '')}`,
        ].join('\n'),
        source,
      });
    } else {
      sendToPrivyr({
        name: String(formData.get('fullName') || ''),
        phone: String(formData.get('phone') || ''),
        message: [
          `Area of Interest: ${String(formData.get('areaOfInterest') || '')}`,
          `Why join: ${String(formData.get('reason') || '')}`,
        ].join('\n'),
        source,
      });
    }

    alert('Thank you for your application! We will get back to you soon.');
    form.reset();
    setIsCapitalModalOpen(false);
    setIsMentorshipModalOpen(false);
  };

  return (
    <>
      <Seo {...PAGE_METADATA.programs} structuredData={getProgramsStructuredData()} />
      <div className="pt-32 pb-24 overflow-hidden">
        <section className="px-6 max-w-7xl mx-auto text-center mb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 px-4 py-2 rounded-full mb-6">
              <Briefcase className="w-4 h-4 text-royal-purple" />
              <span className="text-royal-purple text-[10px] font-black uppercase tracking-[0.2em]">Our Initiatives</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-display font-black text-royal-dark tracking-tighter mb-6 break-words">
              Empowerment <span className="text-royal-purple">Initiatives</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-500 max-w-3xl mx-auto font-medium">
              We offer a range of programs designed to provide the necessary tools, funding, and guidance for young
              women to thrive and lead in their chosen fields.
            </p>
          </motion.div>
        </section>

        <section className="px-6 max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white p-12 rounded-[3rem] border border-slate-100 shadow-xl">
              <div className="w-16 h-16 bg-royal-accent rounded-2xl flex items-center justify-center text-royal-purple mb-8">
                <Zap className="w-8 h-8" />
              </div>
              <h2 className="text-3xl font-display font-black text-royal-dark mb-6">Startup Capital</h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                We give out startup capital to our winners to help them establish and upgrade their businesses
                respectively, ensuring that they have the financial support needed to take meaningful steps toward
                growth and stability.
              </p>
              <button
                onClick={() => setIsCapitalModalOpen(true)}
                className="bg-royal-dark text-white px-8 py-4 rounded-2xl font-bold hover:bg-royal-purple transition-all"
              >
                Apply for Capital
              </button>
            </div>
            <div className="bg-white p-12 rounded-[3rem] border border-slate-100 shadow-xl">
              <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center text-royal-gold mb-8">
                <Users className="w-8 h-8" />
              </div>
              <h2 className="text-3xl font-display font-black text-royal-dark mb-6">Mentorship & Tools</h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Our goal is not only to celebrate winners, but to create lasting impact by building confidence,
                encouraging innovation, and promoting sustainable growth through practical support and resources.
              </p>
              <button
                onClick={() => setIsMentorshipModalOpen(true)}
                className="bg-royal-dark text-white px-8 py-4 rounded-2xl font-bold hover:bg-royal-purple transition-all"
              >
                Join Mentorship
              </button>
            </div>
            <div className="bg-royal-accent/30 p-12 rounded-[3rem] border border-amber-200 shadow-xl lg:col-span-2 flex flex-col md:flex-row items-center gap-12">
              <div className="w-24 h-24 bg-white rounded-[2rem] flex items-center justify-center text-royal-purple shrink-0 shadow-xl">
                <Sparkles className="w-12 h-12" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-3xl font-display font-black text-royal-dark mb-4">Delight Business Empowerment Contest</h2>
                <p className="text-slate-600 text-lg leading-relaxed mb-8">
                  Our flagship beauty and empowerment competition. Join a community of visionary women and stand a
                  chance to win significant grants, global visibility, and life-long mentorship.
                </p>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-3 bg-royal-purple text-white px-10 py-5 rounded-2xl font-black text-lg shadow-xl hover:bg-royal-dark transition-all group"
                >
                  Register for Contest
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Modal isOpen={isCapitalModalOpen} onClose={() => setIsCapitalModalOpen(false)} title="Apply for Startup Capital">
          <form onSubmit={handleSubmit} className="space-y-6" data-source="Website - Startup Capital">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Full Name</label>
                <input
                  required
                  type="text"
                  name="fullName"
                  className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-royal-purple/20 focus:border-royal-purple transition-all"
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Email Address</label>
                <input
                  required
                  type="email"
                  name="email"
                  className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-royal-purple/20 focus:border-royal-purple transition-all"
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Business Name</label>
              <input
                required
                type="text"
                name="businessName"
                className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-royal-purple/20 focus:border-royal-purple transition-all"
                placeholder="Your Venture"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Business Description</label>
              <textarea
                required
                rows={4}
                name="businessDescription"
                className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-royal-purple/20 focus:border-royal-purple transition-all"
                placeholder="Tell us about your business vision..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Funding Goal (NGN)</label>
              <input
                required
                type="number"
                name="fundingGoal"
                className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-royal-purple/20 focus:border-royal-purple transition-all"
                placeholder="Amount needed"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-royal-purple text-white py-5 rounded-2xl font-black text-lg shadow-xl shadow-amber-100 flex items-center justify-center gap-3 hover:bg-royal-dark transition-all"
            >
              Submit Application <Send className="w-5 h-5" />
            </button>
          </form>
        </Modal>

        <Modal isOpen={isMentorshipModalOpen} onClose={() => setIsMentorshipModalOpen(false)} title="Join Mentorship Program">
          <form onSubmit={handleSubmit} className="space-y-6" data-source="Website - Mentorship">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Full Name</label>
                <input
                  required
                  type="text"
                  name="fullName"
                  className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-royal-purple/20 focus:border-royal-purple transition-all"
                  placeholder="Jane Doe"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Phone Number</label>
                <input
                  required
                  type="tel"
                  name="phone"
                  className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-royal-purple/20 focus:border-royal-purple transition-all"
                  placeholder="+234..."
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Area of Interest</label>
              <select
                required
                name="areaOfInterest"
                className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-royal-purple/20 focus:border-royal-purple transition-all"
              >
                <option value="">Select an area...</option>
                <option value="business-strategy">Business Strategy</option>
                <option value="marketing">Marketing & Branding</option>
                <option value="finance">Financial Management</option>
                <option value="tech">Technology & Innovation</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Why do you want to join?</label>
              <textarea
                required
                rows={4}
                name="reason"
                className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-royal-purple/20 focus:border-royal-purple transition-all"
                placeholder="Share your motivation..."
              />
            </div>
            <button
              type="submit"
              className="w-full bg-royal-dark text-white py-5 rounded-2xl font-black text-lg shadow-xl shadow-slate-200 flex items-center justify-center gap-3 hover:bg-royal-purple transition-all"
            >
              Join Program <Send className="w-5 h-5" />
            </button>
          </form>
        </Modal>

        <section className="mt-32 px-6 max-w-4xl mx-auto text-center">
          <p className="text-2xl font-display font-black text-royal-dark italic">
            "Delight Business Empowerment is where potential is discovered, dreams are supported, and success is nurtured."
          </p>
        </section>
      </div>
    </>
  );
};

export default Programs;
